import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Building, FileText, ImageUp, LogIn, Palette, RotateCcw, Settings2, ShieldCheck, Tags, Type, Upload, KeyRound, MessageCircle, Plus, Save, Trash2, UserRound, UsersRound } from "lucide-react";
import { GateForm } from "@/components/GateSettings";
import { toast } from "sonner";
import { ICONS, MenuIcon, applyTheme, applyTypography } from "@/lib/icons";
import { setChannel } from "@/lib/channel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const errText = (e) => { const d = e.response?.data?.detail; return Array.isArray(d) ? d.map((x) => x.msg.replace(/^Value error, /, "")).join(" ") : d || "İşlem başarısız"; };

function Section({ icon: Icon, title, desc, children, testid }) {
  return (
    <section className="panel settings-section" data-testid={testid}>
      <div className="settings-section-head"><span className="settings-icon"><Icon size={18} /></span><div><h3>{title}</h3><p>{desc}</p></div></div>
      {children}
    </section>
  );
}

const Field = ({ label, testid, ...props }) => <label className="settings-field">{label}<input data-testid={testid} {...props} /></label>;

function useSubmit(fn, ok) {
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await fn(); toast.success(ok); } catch (err) { toast.error(errText(err)); }
    setBusy(false);
  };
  return [submit, busy];
}

function ProfileForm({ user, onUser }) {
  const [f, setF] = useState({ name: user.name, email: user.email });
  const [submit, busy] = useSubmit(async () => { const r = await axios.put(`${API}/auth/profile`, f); onUser(r.data); }, "Profil güncellendi");
  return (
    <form onSubmit={submit}>
      <div className="form-row"><Field label="Ad Soyad" testid="profile-name-input" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Field label="E-posta" testid="profile-email-input" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
      <button className="primary-btn" disabled={busy} data-testid="profile-save-button"><Save size={15} />Kaydet</button>
    </form>
  );
}

function PasswordForm() {
  const empty = { current_password: "", new_password: "", confirm: "" };
  const [f, setF] = useState(empty);
  const [submit, busy] = useSubmit(async () => {
    if (f.new_password !== f.confirm) throw { response: { data: { detail: "Yeni şifreler eşleşmiyor" } } };
    await axios.put(`${API}/auth/password`, { current_password: f.current_password, new_password: f.new_password }); setF(empty);
  }, "Şifre değiştirildi");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit}>
      <Field label="Mevcut şifre" testid="current-password-input" type="password" required value={f.current_password} onChange={set("current_password")} />
      <div className="form-row"><Field label="Yeni şifre (en az 8 karakter)" testid="new-password-input" type="password" minLength={8} required value={f.new_password} onChange={set("new_password")} />
        <Field label="Yeni şifre (tekrar)" testid="confirm-password-input" type="password" required value={f.confirm} onChange={set("confirm")} /></div>
      <button className="primary-btn" disabled={busy} data-testid="password-save-button"><KeyRound size={15} />Şifreyi değiştir</button>
    </form>
  );
}

function ReminderContacts({ rows, onChange }) {
  const upd = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="settings-field" data-testid="reminder-contacts">
      Ödeme hatırlatmalarını WhatsApp'tan alacak kişiler (en fazla 4)
      {rows.map((r, i) => (
        <div className="reminder-contact-row" key={i} data-testid={`reminder-contact-row-${i}`}>
          <input data-testid={`reminder-contact-name-${i}`} value={r.name} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Ad (örn. Tarık)" maxLength={60} />
          <input data-testid={`reminder-contact-phone-${i}`} type="tel" value={r.phone} onChange={(e) => upd(i, "phone", e.target.value)} placeholder="05xx xxx xx xx" maxLength={30} />
          <button type="button" className="icon-btn danger" onClick={() => onChange(rows.filter((_, j) => j !== i))} data-testid={`reminder-contact-remove-${i}`}><Trash2 size={15} /></button>
        </div>
      ))}
      {rows.length < 4 && <button type="button" className="outline-btn reminder-add" onClick={() => onChange([...rows, { name: "", phone: "" }])} data-testid="reminder-contact-add"><Plus size={14} />Kişi ekle</button>}
    </div>
  );
}

function PdfSigners({ names, onChange }) {
  const rows = [0, 1, 2].map((i) => names[i] || "");
  return (
    <div className="settings-field" data-testid="pdf-signers">
      Satış Kaydı PDF'inin altındaki isimler (en fazla 3, boş bırakılan görünmez)
      <div className="signer-row">
        {rows.map((n, i) => <input key={i} data-testid={`pdf-signer-input-${i}`} value={n} maxLength={60} placeholder={`${i + 1}. isim`} onChange={(e) => onChange(rows.map((x, j) => (j === i ? e.target.value : x)))} />)}
      </div>
    </div>
  );
}

function OfficeForm({ onOffice }) {
  const [f, setF] = useState(null);
  useEffect(() => { axios.get(`${API}/settings/office`).then((r) => setF((p) => p ?? { ...r.data, reminder_contacts: r.data.reminder_contacts?.length ? r.data.reminder_contacts : r.data.reminder_phone ? [{ name: "", phone: r.data.reminder_phone }] : [{ name: "", phone: "" }] })).catch(() => {}); }, []);
  const [submit, busy] = useSubmit(async () => {
    const contacts = f.reminder_contacts.map((c) => ({ name: c.name.trim(), phone: c.phone.trim() })).filter((c) => c.name || c.phone);
    if (contacts.some((c) => !c.phone)) throw { response: { data: { detail: "Hatırlatma kişilerinin telefon numarasını girin" } } };
    const r = await axios.put(`${API}/settings/office`, { ...Object.fromEntries(Object.entries(f).filter(([k]) => !k.startsWith("login_"))), default_team_share: Number(f.default_team_share), reminder_contacts: contacts, reminder_phone: "", pdf_signers: (f.pdf_signers || []).map((n) => n.trim()) });
    onOffice(r.data); setChannel(r.data.whatsapp_channel); setF((p) => ({ ...p, whatsapp_channel: r.data.whatsapp_channel }));
  }, "Ofis bilgileri kaydedildi");
  if (!f) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit}>
      <div className="form-row"><Field label="Ofis adı" testid="office-name-input" required value={f.name} onChange={set("name")} />
        <Field label="Telefon" testid="office-phone-input" value={f.phone} onChange={set("phone")} placeholder="0 (312) 000 00 00" /></div>
      <div className="form-row"><Field label="Adres" testid="office-address-input" value={f.address} onChange={set("address")} />
        <Field label="Vergi no" testid="office-tax-input" value={f.tax_no} onChange={set("tax_no")} /></div>
      <Field label="Genel Bakış karşılama adı (Günaydın, ...)" testid="office-greeting-input" value={f.greeting_name} onChange={set("greeting_name")} placeholder="Emlak Sepeti" />
      <ReminderContacts rows={f.reminder_contacts} onChange={(rows) => setF({ ...f, reminder_contacts: rows })} />
      <PdfSigners names={f.pdf_signers || []} onChange={(names) => setF({ ...f, pdf_signers: names })} />
      <Field label="WhatsApp mesajındaki yorum linki" testid="office-review-link-input" type="url" value={f.review_link || ""} onChange={set("review_link")} placeholder="https://..." />
      <Field label="WhatsApp kanal linki" testid="office-channel-input" maxLength={200} value={f.whatsapp_channel || ""} onChange={set("whatsapp_channel")} placeholder="https://whatsapp.com/channel/..." />
      <p className="form-hint" data-testid="office-channel-hint">PDF raporların sonunda karekodla, Hazır Cevaplar'da, toplu mesajda ve WhatsApp profilinde kullanılır. Boş bırakırsanız hiçbir yerde görünmez.</p>
      <Field label="Varsayılan ekip payı (%)" testid="office-default-share-input" type="number" min="0" max="100" step="0.01" required value={f.default_team_share} onChange={(e) => setF({ ...f, default_team_share: e.target.value })} />
      <button className="primary-btn" disabled={busy} data-testid="office-save-button"><Save size={15} />Kaydet</button>
    </form>
  );
}

function TeamRow({ team, index, onChange }) {
  const [name, setName] = useState(team.name), [phone, setPhone] = useState(team.phone || "");
  const save = () => axios.put(`${API}/teams/${team.id}`, { name, phone }).then(() => { toast.success("Ekip güncellendi"); onChange(); }).catch((e) => toast.error(errText(e)));
  const remove = () => window.confirm(`${team.name} silinsin mi?`) && axios.delete(`${API}/teams/${team.id}`).then(() => { toast.success("Ekip silindi"); onChange(); }).catch((e) => toast.error(errText(e)));
  return (
    <div className="settings-team-row" data-testid={`settings-team-row-${index}`}>
      <input data-testid={`settings-team-name-${index}`} value={name} onChange={(e) => setName(e.target.value)} />
      <input data-testid={`settings-team-phone-${index}`} type="tel" value={phone} maxLength={30} onChange={(e) => setPhone(e.target.value)} placeholder="WhatsApp 05xx xxx xx xx" />
      <button type="button" className="outline-btn" disabled={(name === team.name && phone === (team.phone || "")) || !name.trim()} onClick={save} data-testid={`settings-team-save-${index}`}><Save size={14} />Kaydet</button>
      <button type="button" className="icon-btn danger" onClick={remove} data-testid={`settings-team-delete-${index}`}><Trash2 size={15} /></button>
    </div>
  );
}

function TeamsManager() {
  const [teams, setTeams] = useState([]), [name, setName] = useState(""), [phone, setPhone] = useState("");
  const load = () => axios.get(`${API}/teams`).then((r) => setTeams(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = (e) => { e.preventDefault(); axios.post(`${API}/teams`, { name, phone }).then(() => { setName(""); setPhone(""); toast.success("Ekip eklendi"); load(); }).catch((err) => toast.error(errText(err))); };
  return (
    <>
      {teams.map((t, i) => <TeamRow key={`${t.id}-${t.name}`} team={t} index={i} onChange={load} />)}
      <form className="settings-team-row add" onSubmit={add}>
        <input data-testid="new-team-name-input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Yeni ekip adı" />
        <input data-testid="new-team-phone-input" type="tel" value={phone} maxLength={30} onChange={(e) => setPhone(e.target.value)} placeholder="WhatsApp 05xx xxx xx xx" />
        <button className="primary-btn" data-testid="add-team-button"><Plus size={15} />Ekip ekle</button>
      </form>
    </>
  );
}

function WhatsAppStatus({ version }) {
  const [ok, setOk] = useState(null), [tpl, setTpl] = useState({});
  useEffect(() => { axios.get(`${API}/whatsapp/status`).then((r) => { setOk(r.data.configured); setTpl(r.data.templates || {}); }).catch(() => {}); }, [version]);
  if (ok === null) return null;
  const TPL = [["thanks", "Teşekkür mesajı"], ["reminder", "Ödeme hatırlatması (ofis)"], ["rent", "Kira hatırlatması (kiracı)"], ["report", "Aylık satış kaydı (ekip)"], ["campaign", "Toplu mesaj (kampanya)"]];
  return (
    <div className="wa-status" data-testid="whatsapp-status">
      <span className={ok ? "wa-badge ok" : "wa-badge"}><MessageCircle size={12} />{ok ? "Twilio WhatsApp bağlı" : "Twilio WhatsApp bilgileri henüz girilmedi"}</span>
      <div className="wa-templates" data-testid="whatsapp-templates">{TPL.map(([k, l]) => <span key={k} className={tpl[k] ? "wa-badge ok" : "wa-badge"} data-testid={`whatsapp-template-${k}`}>{l}: {tpl[k] ? "bağlı" : "bekliyor"}</span>)}</div>
      <p className="form-hint">Mesaj, Twilio'da onaylı şablonla gönderilir: {"{{1}}"} = kişinin adı, {"{{2}}"} = yorum linki (Ofis bilgileri bölümünden).</p>
    </div>
  );
}

const TWILIO_INPUTS = [["account_sid", "Account SID", "AC… (Twilio Console → Account Info)", true], ["whatsapp_from", "WhatsApp gönderen numara", "+90 530 689 96 06", true],
  ["content_sid", "Teşekkür şablon kodu", "HX…", true], ["reminder_content_sid", "Ödeme hatırlatma şablon kodu", "HX… (isteğe bağlı)"],
  ["rent_content_sid", "Kira hatırlatma şablon kodu", "HX… (isteğe bağlı)"], ["report_content_sid", "Aylık satış kaydı şablon kodu", "HX… (isteğe bağlı)"], ["campaign_content_sid", "Toplu mesaj şablon kodu", "HX… (isteğe bağlı)"], ["listing_content_sid", "İlan onay/ret bildirimi şablon kodu", "HX… (üye ilanları için)"]];

const LINE_STATUS = { ONLINE: ["Twilio'da aktif", "ok"], OFFLINE: ["Twilio'da kapalı", ""] };
const LineStatus = ({ status, testid }) => { const [t, c] = LINE_STATUS[status] || ["Twilio'da henüz kayıtlı değil", ""]; return <span className={`wa-badge ${c}`} data-testid={testid}>{t}</span>; };

function LinesEditor({ f, setF }) {
  const lines = f.extra_lines || [];
  const upd = (i, k) => (e) => setF({ ...f, extra_lines: lines.map((l, j) => (j === i ? { ...l, [k]: e.target.value } : l)) });
  return (
    <div className="lines-editor" data-testid="whatsapp-lines">
      <small className="team-label">WHATSAPP HATLARI</small>
      <div className="line-row">
        <Field label="Ana hat adı" testid="twilio-main-label-input" value={f.main_label} onChange={(e) => setF({ ...f, main_label: e.target.value })} placeholder="Ana hat" maxLength={40} />
        <span className="line-number">{f.whatsapp_from || "—"}</span><LineStatus status={f.main_status} testid="main-line-status" />
      </div>
      {lines.map((l, i) => (
        <div className="line-row" key={i} data-testid={`extra-line-${i}`}>
          <Field label="Hat adı" testid={`extra-line-label-${i}`} value={l.label} onChange={upd(i, "label")} placeholder="Kiralama hattı" maxLength={40} />
          <Field label="Numara" testid={`extra-line-number-${i}`} required value={l.number} onChange={upd(i, "number")} placeholder="0538 093 02 04" />
          {"status" in l && <LineStatus status={l.status} testid={`extra-line-status-${i}`} />}
          <button type="button" className="icon-btn" onClick={() => setF({ ...f, extra_lines: lines.filter((_, j) => j !== i) })} title="Hattı kaldır" data-testid={`extra-line-remove-${i}`}><Trash2 size={15} /></button>
        </div>
      ))}
      {lines.length < 3 && <button type="button" className="outline-btn" onClick={() => setF({ ...f, extra_lines: [...lines, { number: "", label: "" }] })} data-testid="add-line-button"><Plus size={15} />Hat ekle</button>}
      <p className="form-hint">Otomatik mesajlar (teşekkür, hatırlatmalar, aylık rapor) her zaman ana hattan gider. Ek hatlara gelen mesajlar Mesajlar sayfasında ayrı sekmede görünür ve aynı hattan cevap verilir.</p>
    </div>
  );
}

function TwilioForm({ onSaved }) {
  const [f, setF] = useState(null), [hint, setHint] = useState("");
  const fill = (d) => { setHint(d.auth_token_hint); setF({ ...d, auth_token: "" }); };
  useEffect(() => { axios.get(`${API}/settings/twilio`).then((r) => fill(r.data)).catch(() => {}); }, []);
  const [submit, busy] = useSubmit(async () => { const r = await axios.put(`${API}/settings/twilio`, f); fill(r.data); onSaved(); }, "Twilio bilgileri doğrulandı ve kaydedildi");
  if (!f) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="twilio-form" data-testid="twilio-form">
      <small className="team-label">TWILIO BİLGİLERİ</small>
      <div className="form-row">
        <Field label="Account SID" testid="twilio-account-sid-input" required value={f.account_sid} onChange={set("account_sid")} placeholder={TWILIO_INPUTS[0][2]} autoComplete="off" />
        <Field label="Auth Token" testid="twilio-auth-token-input" type="password" value={f.auth_token} onChange={set("auth_token")} required={!hint} autoComplete="new-password" placeholder={hint ? `Kayıtlı (${hint}) – değiştirmek için yenisini yazın` : "Twilio Console → Account Info → Auth Token"} />
      </div>
      <div className="form-row">{TWILIO_INPUTS.slice(1).map(([k, label, ph, req]) => <Field key={k} label={label} testid={`twilio-${k.replaceAll("_", "-")}-input`} required={req} value={f[k]} onChange={set(k)} placeholder={ph} autoComplete="off" />)}</div>
      <LinesEditor f={f} setF={setF} />
      <p className="form-hint">Kaydet'e bastığınızda bilgiler Twilio ile kontrol edilir; doğruysa hemen kullanılmaya başlar, yeniden yayınlamanız gerekmez.</p>
      <button className="primary-btn" disabled={busy} data-testid="twilio-save-button"><Save size={15} />{busy ? "Kontrol ediliyor…" : "Kaydet ve bağlantıyı test et"}</button>
    </form>
  );
}

function WhatsAppSettings() {
  const [version, setVersion] = useState(0);
  return <><WhatsAppStatus version={version} /><TwilioForm onSaved={() => setVersion((n) => n + 1)} /></>;
}

const LOGIN_FIELDS = [["login_eyebrow", "Sol üst küçük başlık"], ["login_title", "Sol büyük başlık"], ["login_text", "Sol açıklama metni"], ["login_welcome", "Sağ küçük başlık"], ["login_heading", "Sağ başlık"], ["login_sub", "Sağ alt metin"], ["login_splash", "Girişten sonraki karşılama yazısı (5 saniyelik animasyon)"]];

function LoginTextsForm({ onSaved }) {
  const [f, setF] = useState(null);
  useEffect(() => { axios.get(`${API}/settings/office`).then((r) => setF((p) => p ?? r.data)).catch(() => {}); }, []);
  const [submit, busy] = useSubmit(async () => { const r = await axios.put(`${API}/settings/office`, { ...Object.fromEntries(LOGIN_FIELDS.map(([k]) => [k, f[k] ?? ""])) }); onSaved(r.data); }, "Giriş ekranı metinleri kaydedildi");
  if (!f) return null;
  return (
    <form onSubmit={submit}>
      {LOGIN_FIELDS.map(([k, label]) => <Field key={k} label={label} testid={`${k.replace("_", "-")}-input`} maxLength={300} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} />)}
      <button className="primary-btn" disabled={busy} data-testid="login-texts-save-button"><Save size={15} />Kaydet</button>
    </form>
  );
}

function CategoryList({ kind, title, cats, onChange }) {
  const [name, setName] = useState("");
  const add = (e) => { e.preventDefault(); axios.post(`${API}/categories`, { type: kind, name }).then((r) => { setName(""); onChange(r.data); toast.success("Kategori eklendi"); }).catch((err) => toast.error(errText(err))); };
  const remove = (c) => window.confirm(`"${c}" kategorisi silinsin mi? Mevcut kayıtlar etkilenmez.`) && axios.delete(`${API}/categories/${kind}/${encodeURIComponent(c)}`).then((r) => { onChange(r.data); toast.success("Kategori silindi"); }).catch((err) => toast.error(errText(err)));
  return (
    <div className="cat-col" data-testid={`categories-${kind}`}>
      <small className="team-label">{title}</small>
      <div className="cat-chips">{cats.map((c, i) => <span className="cat-chip" key={c} data-testid={`category-${kind}-${i}`}>{c}<button type="button" onClick={() => remove(c)} data-testid={`delete-category-${kind}-${i}`}><Trash2 size={11} /></button></span>)}</div>
      <form className="settings-team-row add" onSubmit={add}><input data-testid={`new-category-${kind}-input`} required maxLength={40} value={name} onChange={(e) => setName(e.target.value)} placeholder="Yeni kategori" /><button className="outline-btn" data-testid={`add-category-${kind}-button`}><Plus size={14} />Ekle</button></form>
    </div>
  );
}

function CategoriesManager() {
  const [cats, setCats] = useState(null);
  useEffect(() => { axios.get(`${API}/categories`).then((r) => setCats(r.data)).catch(() => {}); }, []);
  if (!cats) return null;
  return <div className="cat-grid"><CategoryList kind="income" title="GELİR KATEGORİLERİ" cats={cats.income} onChange={setCats} /><CategoryList kind="expense" title="GİDER KATEGORİLERİ" cats={cats.expense} onChange={setCats} /></div>;
}

const PALETTE = ["#2563eb", "#0f9f6e", "#f97316", "#dc2626", "#d0457f", "#7c3aed", "#0891b2", "#ca8a04", "#102a43", "#475569"];

function MenuEditRow({ item, value, onChange, onUpload, onRemoveUpload }) {
  const [open, setOpen] = useState(false);
  const upload = (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onUpload(item.id, f); };
  return (
    <div className="menu-edit-row" data-testid={`menu-edit-${item.id}`}>
      <button type="button" className="icon-pick-btn" title="İkon seç" onClick={() => setOpen(!open)} data-testid={`menu-icon-pick-${item.id}`}><MenuIcon id={item.id} item={value} fallback={item.icon} /></button>
      <input type="text" maxLength={40} value={value.label || ""} placeholder={item.label} onChange={(e) => onChange({ ...value, label: e.target.value })} data-testid={`menu-label-${item.id}`} />
      <span className="menu-edit-actions">
        <label className="icon-btn" title="Kendi ikonunu yükle" data-testid={`menu-icon-upload-${item.id}`}><Upload size={14} /><input type="file" hidden accept="image/png,image/jpeg,image/webp" onChange={upload} data-testid={`menu-icon-file-${item.id}`} /></label>
        <button type="button" className="icon-btn" title="Varsayılana dön" onClick={() => { onChange({ label: "", icon: "" }); if (value.icon_version) onRemoveUpload(item.id); }} data-testid={`menu-reset-${item.id}`}><RotateCcw size={14} /></button>
      </span>
      {open && (
        <div className="icon-gallery" data-testid={`icon-gallery-${item.id}`}>
          {Object.entries(ICONS).map(([name, Icon]) => (
            <button type="button" key={name} title={name} className={value.icon === name && !value.icon_version ? "active" : ""} onClick={() => { onChange({ ...value, icon: name }); if (value.icon_version) onRemoveUpload(item.id); setOpen(false); }} data-testid={`icon-option-${item.id}-${name}`}><Icon size={17} /></button>
          ))}
        </div>
      )}
    </div>
  );
}

function AppearanceForm({ branding, onAppearance, nav }) {
  const ap = branding?.appearance || { primary_color: null, menu: {} };
  const items = [...nav, { id: "settings", label: "Ayarlar", icon: Settings2 }];
  const [color, setColor] = useState(ap.primary_color);
  const [menu, setMenu] = useState(() => Object.fromEntries(items.map((n) => [n.id, { label: "", icon: "", ...(ap.menu[n.id] || {}) }])));
  const saved = useRef(ap.primary_color); saved.current = ap.primary_color;
  useEffect(() => { applyTheme(color); }, [color]);
  useEffect(() => () => applyTheme(saved.current), []);
  const syncUploads = (a) => { setMenu((m) => Object.fromEntries(Object.entries(m).map(([k, v]) => [k, { ...v, icon_version: a.menu[k]?.icon_version || null }]))); onAppearance(a); setTimeout(() => applyTheme(color), 0); };
  const upload = async (id, file) => {
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) return toast.error("Sadece PNG, JPG veya WEBP yükleyebilirsiniz");
    if (file.size > 1024 * 1024) return toast.error("İkon en fazla 1 MB olabilir");
    try { const fd = new FormData(); fd.append("file", file); const r = await axios.post(`${API}/appearance/icon/${id}`, fd); syncUploads(r.data); toast.success("İkon yüklendi"); } catch (e) { toast.error(errText(e)); }
  };
  const removeUpload = async (id) => { try { const r = await axios.delete(`${API}/appearance/icon/${id}`); syncUploads(r.data); } catch (e) { toast.error(errText(e)); } };
  const [submit, busy] = useSubmit(async () => {
    const body = { primary_color: color || null, menu: Object.fromEntries(Object.entries(menu).map(([k, v]) => [k, { label: v.label || "", icon: v.icon || "" }])) };
    const r = await axios.put(`${API}/appearance`, body); onAppearance(r.data);
  }, "Görünüm kaydedildi");
  return (
    <form onSubmit={submit}>
      <small className="team-label">ANA RENK</small>
      <div className="color-swatches" data-testid="color-swatches">
        {PALETTE.map((c) => <button type="button" key={c} className={color === c ? "swatch active" : "swatch"} style={{ background: c }} onClick={() => setColor(c)} data-testid={`swatch-${c.slice(1)}`} />)}
        <input type="color" className="color-input" value={color || "#2563eb"} onChange={(e) => setColor(e.target.value)} data-testid="color-picker-input" title="Özel renk" />
        <button type="button" className="outline-btn" onClick={() => setColor(null)} data-testid="color-reset-button"><RotateCcw size={13} />Varsayılan</button>
      </div>
      <small className="team-label">MENÜ ADLARI VE İKONLARI</small>
      {items.map((n) => <MenuEditRow key={n.id} item={n} value={menu[n.id]} onChange={(v) => setMenu({ ...menu, [n.id]: v })} onUpload={upload} onRemoveUpload={removeUpload} />)}
      <p className="form-hint" style={{ marginTop: 10 }}>İkona tıklayıp galeriden seçin veya yükleme simgesiyle kendi ikonunuzu (PNG/JPG/WEBP, en fazla 1 MB) yükleyin. Adı boş bırakırsanız varsayılan ad kullanılır. Renk seçimi kaydetmeden önce önizlenir.</p>
      <button className="primary-btn" disabled={busy} data-testid="appearance-save-button"><Save size={15} />Görünümü kaydet</button>
    </form>
  );
}

const FONTS = [{ id: "rajdhani", label: "Rajdhani", family: "Rajdhani, sans-serif" }, { id: "manrope", label: "Manrope", family: "Manrope, sans-serif" }];
const SIZES = [{ id: "sm", label: "Küçük", s: "11px" }, { id: "md", label: "Normal", s: "13px" }, { id: "lg", label: "Büyük", s: "16px" }];

function TypographyForm({ branding, onAppearance }) {
  const ap = branding?.appearance || {};
  const [font, setFont] = useState(ap.font || "rajdhani"), [size, setSize] = useState(ap.font_size || "md");
  const saved = useRef(ap); saved.current = ap;
  useEffect(() => { applyTypography(font, size); }, [font, size]);
  useEffect(() => () => applyTypography(saved.current.font, saved.current.font_size), []);
  const [submit, busy] = useSubmit(async () => {
    const r = await axios.put(`${API}/appearance/typography`, { font, font_size: size }); onAppearance(r.data);
  }, "Yazı tipi kaydedildi");
  return (
    <form onSubmit={submit}>
      <small className="team-label">YAZI TİPİ</small>
      <div className="font-options" data-testid="font-options">
        {FONTS.map((f) => (
          <button type="button" key={f.id} className={font === f.id ? "font-card active" : "font-card"} style={{ fontFamily: f.family }} onClick={() => setFont(f.id)} data-testid={`font-option-${f.id}`}>
            <b>Aa</b><span><strong>{f.label}{f.id === "rajdhani" && <em>Varsayılan</em>}</strong><small>Emlak Sepeti · 1.250.000 ₺</small></span>
          </button>
        ))}
      </div>
      <small className="team-label">YAZI BOYUTU</small>
      <div className="size-options" data-testid="font-size-options">
        {SIZES.map((s) => <button type="button" key={s.id} className={size === s.id ? "filter-btn active" : "filter-btn"} style={{ "--s": s.s }} onClick={() => setSize(s.id)} data-testid={`font-size-${s.id}`}><b>A</b>{s.label}</button>)}
      </div>
      <p className="form-hint">Seçiminiz kaydetmeden önce önizlenir. Kaydettiğinizde tüm ofis kullanıcıları bu görünümü görür; PDF raporları da seçilen yazı tipiyle oluşturulur.</p>
      <button className="primary-btn" disabled={busy} data-testid="typography-save-button"><Save size={15} />Yazı tipini kaydet</button>
    </form>
  );
}

function LetterheadForm() {
  const [info, setInfo] = useState(null), [m, setM] = useState(null), [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/settings/letterhead`).then((r) => setInfo(r.data)).catch(() => {});
    axios.get(`${API}/settings/office`).then((r) => setM({ pdf_top_mm: r.data.pdf_top_mm, pdf_bottom_mm: r.data.pdf_bottom_mm })).catch(() => {});
  }, []);
  const upload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg", "application/pdf"].includes(file.type)) return toast.error("Sadece PNG, JPG veya PDF yükleyebilirsiniz");
    if (file.size > 10 * 1024 * 1024) return toast.error("Dosya en fazla 10 MB olabilir");
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const r = await axios.post(`${API}/settings/letterhead`, fd); setInfo(r.data); toast.success("Antetli kâğıt yüklendi"); }
    catch (err) { toast.error(errText(err)); }
    setBusy(false);
  };
  const remove = async () => {
    if (!window.confirm("Antetli kâğıt kaldırılsın mı?")) return;
    try { const r = await axios.delete(`${API}/settings/letterhead`); setInfo(r.data); toast.success("Antetli kâğıt kaldırıldı"); } catch (err) { toast.error(errText(err)); }
  };
  const [submit, saving] = useSubmit(async () => { await axios.put(`${API}/settings/office`, { pdf_top_mm: Number(m.pdf_top_mm), pdf_bottom_mm: Number(m.pdf_bottom_mm) }); }, "PDF boşlukları kaydedildi");
  return (
    <div className="logo-form">
      <div className="letterhead-file" data-testid="letterhead-status">{info?.name ? <><FileText size={16} /><b>{info.name}</b></> : <span>Antetli kâğıt yüklenmedi. PDF'lerde logo ve ofis bilgileriyle hazır bir başlık kullanılır.</span>}</div>
      <div className="logo-actions">
        <label className={busy ? "primary-btn disabled" : "primary-btn"} data-testid="letterhead-upload-button"><FileText size={15} />{busy ? "Yükleniyor…" : info?.name ? "Değiştir" : "Antetli kâğıt yükle"}
          <input type="file" accept="image/png,image/jpeg,application/pdf" hidden disabled={busy} onChange={upload} data-testid="letterhead-file-input" /></label>
        {info?.name && <button type="button" className="outline-btn" onClick={remove} data-testid="letterhead-remove-button"><Trash2 size={14} />Kaldır</button>}
      </div>
      {m && (
        <form onSubmit={submit}>
          <div className="form-row">
            <Field label="Üst boşluk (mm)" testid="pdf-top-input" type="number" min="0" max="150" required value={m.pdf_top_mm} onChange={(e) => setM({ ...m, pdf_top_mm: e.target.value })} />
            <Field label="Alt boşluk (mm)" testid="pdf-bottom-input" type="number" min="0" max="150" required value={m.pdf_bottom_mm} onChange={(e) => setM({ ...m, pdf_bottom_mm: e.target.value })} />
          </div>
          <button className="outline-btn" disabled={saving} data-testid="pdf-margins-save-button"><Save size={14} />Boşlukları kaydet</button>
        </form>
      )}
      <p className="form-hint">A4 boyutunda PNG, JPG veya PDF (ilk sayfa kullanılır), en fazla 10 MB. Yazılar antetin üst ve alt boşlukları arasına yerleşir; boşlukları antetinize göre ayarlayın.</p>
    </div>
  );
}

function LogoForm({ branding, onBranding }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) return toast.error("Sadece PNG, JPG veya WEBP yükleyebilirsiniz");
    if (file.size > 5 * 1024 * 1024) return toast.error("Logo en fazla 5 MB olabilir");
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const r = await axios.post(`${API}/settings/logo`, fd); onBranding(r.data); toast.success("Logo güncellendi"); }
    catch (err) { toast.error(errText(err)); }
    setBusy(false);
  };
  const remove = async () => {
    if (!window.confirm("Logo kaldırılsın mı?")) return;
    try { const r = await axios.delete(`${API}/settings/logo`); onBranding(r.data); toast.success("Logo kaldırıldı"); } catch (err) { toast.error(errText(err)); }
  };
  const has = !!branding?.logo_version;
  return (
    <div className="logo-form">
      <div className="logo-preview" data-testid="logo-preview">{has ? <img src={`${API}/branding/logo?v=${branding.logo_version}`} alt="Logo" /> : <span>Logo yüklenmedi</span>}</div>
      <div className="logo-actions">
        <label className={busy ? "primary-btn disabled" : "primary-btn"} data-testid="logo-upload-button"><ImageUp size={15} />{busy ? "Yükleniyor…" : has ? "Logoyu değiştir" : "Logo yükle"}
          <input type="file" accept="image/png,image/jpeg,image/webp" hidden disabled={busy} onChange={upload} data-testid="logo-file-input" /></label>
        {has && <button type="button" className="outline-btn" onClick={remove} data-testid="logo-remove-button"><Trash2 size={14} />Kaldır</button>}
      </div>
      <p className="form-hint">PNG, JPG veya WEBP, en fazla 5 MB. Şeffaf arka planlı, yatay logo en iyi sonucu verir. Sol menüde, giriş ekranında ve tarayıcı sekmesinde görünür.</p>
    </div>
  );
}

export default function Settings({ user, onUser, onOffice, branding, onBranding, onAppearance, nav }) {
  return (
    <>
      <div className="page-title"><div><span className="eyebrow">HESAP & OFİS</span><h1 data-testid="page-title">Ayarlar</h1><p>Profilinizi, şifrenizi, ofis bilgilerinizi ve ekiplerinizi yönetin.</p></div></div>
      <div className="settings-grid">
        <Section icon={UserRound} title="Profil" desc="Giriş e-postanız ve görünen adınız" testid="settings-profile"><ProfileForm user={user} onUser={onUser} /></Section>
        <Section icon={KeyRound} title="Şifre değiştir" desc="Güvenliğiniz için güçlü bir şifre seçin" testid="settings-password"><PasswordForm /></Section>
        <Section icon={ShieldCheck} title="Güvenlik şifreleri (3 adımlı giriş)" desc="Panel girişinden önce sırayla sorulan iki ek şifre" testid="settings-gate"><GateForm /></Section>
        <Section icon={Palette} title="Menü ve renkler" desc="Menü adlarını, ikonlarını ve uygulamanın ana rengini değiştirin" testid="settings-appearance"><AppearanceForm branding={branding} onAppearance={onAppearance} nav={nav} /></Section>
        <Section icon={Type} title="Yazı tipi" desc="Uygulamanın ve PDF raporlarının yazı tipi ile yazı boyutu" testid="settings-typography"><TypographyForm branding={branding} onAppearance={onAppearance} /></Section>
        <Section icon={ImageUp} title="Logo" desc="Emlak Sepeti logonuzu yükleyin" testid="settings-logo"><LogoForm branding={branding} onBranding={onBranding} /></Section>
        <Section icon={Tags} title="Kategoriler" desc="Gelir & Gider ve Ödeme Takvimi'nde seçilen kategoriler" testid="settings-categories"><CategoriesManager /></Section>
        <Section icon={FileText} title="Antetli kâğıt (PDF)" desc="Sözleşme özeti ve aylık ekip raporu bu kâğıda basılır" testid="settings-letterhead"><LetterheadForm /></Section>
        <Section icon={LogIn} title="Giriş ekranı metinleri" desc="Giriş sayfasındaki yazılar. Boş bırakılan alan gösterilmez." testid="settings-login-texts"><LoginTextsForm onSaved={onOffice} /></Section>
        <Section icon={Building} title="Ofis bilgileri" desc="Emlak ofisinizin iletişim ve vergi bilgileri" testid="settings-office"><OfficeForm onOffice={onOffice} /></Section>
        <Section icon={MessageCircle} title="WhatsApp mesajı" desc="Otomatik mesajlar: teşekkür (tapu/teslim sonrası), ödeme hatırlatması, kiracıya kira hatırlatması, ekibe aylık satış kaydı" testid="settings-whatsapp"><WhatsAppSettings /></Section>
        <Section icon={UsersRound} title="Ekip yönetimi" desc="Her ayın 1'inde saat 12:00'de geçen ayın satış kaydı PDF'i bu WhatsApp numaralarına gönderilir. Sözleşmesi olan ekip silinemez." testid="settings-teams"><TeamsManager /></Section>
      </div>
    </>
  );
}
